import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/cn';
import { diffWords, hasChanges } from '@/lib/textDiff';
import type { CopyItem, CopyLocale, CopyShape, CopyValue } from '@/schemas/admin/copy';

const LOCALE_DIR: Record<CopyLocale, 'ltr' | 'rtl'> = { en: 'ltr', ar: 'rtl', 'eg-ar': 'rtl' };

interface LeafPair {
  path: string;
  label: string;
  before: string;
  after: string;
}

/**
 * Pair the leaves of two copy values by position/field name. Lists are locked
 * to the baseline's item count and field set, so pairing by index is exact
 * rather than a guess — a mismatch can only come from a value the server would
 * already have rejected.
 */
export function copyLeafPairs(
  shape: CopyShape,
  before: CopyValue | null | undefined,
  after: CopyValue | null | undefined
): LeafPair[] {
  if (shape === 'string') {
    return [
      {
        path: '',
        label: '',
        before: typeof before === 'string' ? before : '',
        after: typeof after === 'string' ? after : '',
      },
    ];
  }
  if (shape === 'string_list') {
    const a = Array.isArray(before) ? (before as string[]) : [];
    const b = Array.isArray(after) ? (after as string[]) : [];
    const n = Math.max(a.length, b.length);
    return Array.from({ length: n }, (_, i) => ({
      path: `[${i}]`,
      label: String(i + 1),
      before: a[i] ?? '',
      after: b[i] ?? '',
    }));
  }
  const a = Array.isArray(before) ? (before as CopyItem[]) : [];
  const b = Array.isArray(after) ? (after as CopyItem[]) : [];
  const n = Math.max(a.length, b.length);
  const out: LeafPair[] = [];
  for (let i = 0; i < n; i++) {
    const itemA = a[i] ?? {};
    const itemB = b[i] ?? {};
    const names = Array.from(new Set([...Object.keys(itemA), ...Object.keys(itemB)]));
    for (const name of names) {
      const fa = itemA[name];
      const fb = itemB[name];
      if (Array.isArray(fa) || Array.isArray(fb)) {
        // A list-valued field pairs entry by entry — its length is locked too.
        const la = Array.isArray(fa) ? fa : [];
        const lb = Array.isArray(fb) ? fb : [];
        for (let j = 0; j < Math.max(la.length, lb.length); j++) {
          out.push({
            path: `[${i}].${name}[${j}]`,
            label: `${i + 1} · ${name} · ${j + 1}`,
            before: la[j] ?? '',
            after: lb[j] ?? '',
          });
        }
        continue;
      }
      out.push({
        path: `[${i}].${name}`,
        label: `${i + 1} · ${name}`,
        before: fa ?? '',
        after: fb ?? '',
      });
    }
  }
  return out;
}

interface BaselineDiffProps {
  shape: CopyShape;
  locale: CopyLocale;
  /** What the site renders today — the published override, or the baseline. */
  before: CopyValue | null | undefined;
  beforeLabel: string;
  /** What the editor has in the form right now. */
  after: CopyValue | null | undefined;
  afterLabel: string;
  /** Hide leaves that didn't change. On by default — 20-field items are wide. */
  changedOnly?: boolean;
  className?: string;
}

/**
 * Side-by-side, word-level diff. There is no live preview of a draft (plan
 * Q3), so this is the only place an editor can see what an edit actually does
 * before it goes live.
 */
export function BaselineDiff({
  shape,
  locale,
  before,
  beforeLabel,
  after,
  afterLabel,
  changedOnly = true,
  className,
}: BaselineDiffProps) {
  const { t } = useTranslation('copy');
  const dir = LOCALE_DIR[locale];

  const rows = React.useMemo(() => {
    const pairs = copyLeafPairs(shape, before, after);
    return pairs
      .map((p) => ({ ...p, segments: diffWords(p.before, p.after) }))
      .filter((p) => (changedOnly ? hasChanges(p.segments) : true));
  }, [shape, before, after, changedOnly]);

  if (rows.length === 0) {
    return (
      <p className={cn('text-xs text-[color:var(--color-fg-muted)]', className)}>
        {t('diff.noChanges')}
      </p>
    );
  }

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex flex-wrap items-center gap-3 text-[11px] text-[color:var(--color-fg-muted)]">
        <span className="flex items-center gap-1">
          <span
            aria-hidden
            className="inline-block size-2.5 rounded-full bg-[color:var(--color-danger-soft)] ring-1 ring-[color:var(--color-danger)]"
          />
          {t('diff.removed')}
        </span>
        <span className="flex items-center gap-1">
          <span
            aria-hidden
            className="inline-block size-2.5 rounded-full bg-[color:var(--color-success-soft)] ring-1 ring-[color:var(--color-success)]"
          />
          {t('diff.added')}
        </span>
      </div>
      <ul className="space-y-3">
        {rows.map((row) => (
          <li
            key={row.path}
            className="rounded-[var(--radius-md)] border border-[color:var(--color-border)] bg-[color:var(--color-surface-2)] p-3"
          >
            {row.label ? (
              <p dir="ltr" className="mb-1 font-mono text-[11px] text-[color:var(--color-fg-muted)]">
                {row.label}
              </p>
            ) : null}
            <p className="mb-1 text-[11px] uppercase tracking-wide text-[color:var(--color-fg-muted)]">
              {beforeLabel} → {afterLabel}
            </p>
            <p dir={dir} className="text-sm leading-relaxed">
              {row.segments.map((seg, i) => (
                <span
                  key={`${row.path}-${i}`}
                  className={cn(
                    seg.op === 'remove' &&
                      'bg-[color:var(--color-danger-soft)] text-[color:var(--color-danger)] line-through decoration-1',
                    seg.op === 'add' &&
                      'bg-[color:var(--color-success-soft)] text-[color:var(--color-success)]'
                  )}
                >
                  {seg.op === 'remove' ? (
                    <span className="sr-only">{t('diff.removedSr')} </span>
                  ) : null}
                  {seg.op === 'add' ? <span className="sr-only">{t('diff.addedSr')} </span> : null}
                  {seg.text}
                </span>
              ))}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}
